// External Packages
import { Fragment, useContext, useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

// Internal Packages
import { AuthContext } from "../../context/auth-context";
import { fetchCustomerById } from "../../api/fetch";
import {
  addToDNR,
  editCustomer,
  startScreening,
  checkScreeningStatus,
  sendMessage,
} from "../../api/add";

// External Components
import { FaEdit } from "react-icons/fa";
import { FiCheck } from "react-icons/fi";
import { AiOutlineClose } from "react-icons/ai";
import { MdOutlineDocumentScanner } from "react-icons/md";

// Internal Components
import LoadingSpinnerWrapper from "../UI/LoadingSpinnerWrapper";
import DetailPair from "../UI/DetailPair";
import Heading from "../UI/Heading";
import MessageModel from "./MessageModal";
import DropDownBanner from "../UI/DropDownBanner";
import LinkPair from "../UI/LinkPair";
import uploadFile from "../../util/upload-file";

/*
  * Customer Info Page
  This page displays all information about a customer.
*/

export default function CustomerInfo() {
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  // Initialize States
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [warning, setWarning] = useState(false);
  const [edit, setEdit] = useState(false);
  const [messageModal, setMessageModal] = useState(false);

  // ========================================================== FETCH CUSTOMER
  const fetchCustomer = async () => {
    try {
      setLoading(true);
      const data = await fetchCustomerById(user, id);
      setCustomer(data);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setWarning(err.message);
      console.log(err);
    }
  };

  // ========================================================== EDIT CUSTOMER
  const editHandler = async () => {
    try {
      setLoading(true);
      const payload = {
        customerId: id,
        phone: document.getElementById("Phone").value || "",
        dob: document.getElementById("DOB").value || "",
        address: document.getElementById("Address").value || "",
        drivers_license_number:
          document.getElementById("License Number").value || "",
        drivers_license_exp:
          document.getElementById("License Expiration").value || "",
      };
      if (document.getElementById("License Front").files[0]) {
        const path = `users/${customer.id}/license_front.${document
          .getElementById("License Front")
          .files[0].name.split(".")
          .pop()}`;
        payload.drivers_license_front = await uploadFile(
          path,
          document.getElementById("License Front").files[0]
        );
      }
      if (document.getElementById("License Back").files[0]) {
        const path = `users/${customer.id}/license_back.${document
          .getElementById("License Back")
          .files[0].name.split(".")
          .pop()}`;
        payload.drivers_license_back = await uploadFile(
          path,
          document.getElementById("License Back").files[0]
        );
      }
      const response = await editCustomer(user, payload);
      setEdit(false);
      fetchCustomer();
    } catch (err) {
      console.log(err);
      setWarning(err.message);
      setLoading(false);
    }
  };

  // ========================================================== DO NOT RENT CUSTOMER
  const addToDNRHandler = async () => {
    try {
      setLoading(true);
      await addToDNR(user, { customerId: id });
      fetchCustomer();
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setWarning(err.message);
      console.log(err);
    }
  };

  // ========================================================== START CUSTOMER SCREENING
  const screeningHandler = async () => {
    try {
      setLoading(true);
      await startScreening(user, { customerId: id });
      fetchCustomer();
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setWarning(err.message);
      console.log(err);
    }
  };

  // ========================================================== CHECK SCREENING STATUS
  const checkScreeningStatusHandler = async () => {
    try {
      setLoading(true);
      await checkScreeningStatus(user, { customerId: id });
      fetchCustomer();
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setWarning(err.message);
      console.log(err);
    }
  };

  // ========================================================== SEND SCREENING LINK
  const sendScreeningLinkHandler = async () => {
    try {
      setLoading(true);
      const message = `Please click the following link to complete your driver's license verification: ${generateIVMLink()}`;
      await sendMessage(user, customer.id, message);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setWarning(err.message);
      console.log(err);
    }
  };

  // ========================================================== GENERATE IVM LINK
  const generateIVMLink = () => {
    let link = `${process.env.REACT_APP_DIGISURE_IVM_URL}`;
    link += `&prefill[email]=${customer.email?.trim().replace(/\s/g, "")}`;
    link += `&prefill[phone]=${customer.phone?.trim().replace(/\s/g, "")}`;
    link += `&prefill[first_name]=${customer.firstName
      .trim()
      .replace(/\s/g, "%20")}`;
    link += `&prefill[last_name]=${customer.lastName
      .trim()
      .replace(/\s/g, "%20")}`;
    link += `&prefill[partner_driver_id]=${customer.id}`;

    return link;
  };

  // Fetch Customer Information on Page Load
  useEffect(() => {
    fetchCustomer();
  }, []);

  // Reset Warning
  useEffect(() => {
    if (loading) setWarning();
  }, [loading]);

  if (!id)
    return (
      <div className="container">
        <p className="text-stone-400">Customer ID Not Provided!</p>
      </div>
    );

  if (!loading && !customer)
    return (
      <div className="container">
        <p className="text-stone-400">Customer Not Found!</p>
      </div>
    );

  return (
    <div className="container">
      <MessageModel
        open={messageModal}
        setOpen={setMessageModal}
        customer={customer}
      />
      <Heading title="Customer Information">
        {edit ? (
          <Fragment>
            <AiOutlineClose
              onClick={() => setEdit(false)}
              className="text-lg text-white cursor-pointer md:block hover:text-stone-400"
            />
            <FiCheck
              onClick={editHandler}
              className="text-lg text-white cursor-pointer md:block hover:text-stone-400"
            />
          </Fragment>
        ) : (
          <Fragment>
            <FaEdit
              onClick={() => setEdit(true)}
              className="text-lg text-white cursor-pointer md:block hover:text-stone-400"
            />
          </Fragment>
        )}
      </Heading>

      {loading ? (
        <LoadingSpinnerWrapper />
      ) : (
        <div className="info">
          {customer.dnr && (
            <div className="absolute px-3 py-2 bg-red-600 rounded-md left-[55%] text-xs">
              Do Not Rent!
            </div>
          )}
          <DetailPair label="Customer Id" value={customer.id} />
          <DetailPair
            label="Name"
            value={customer.firstName + " " + customer.lastName}
          />
          <DetailPair label="Email" value={customer.email} />
          <DetailPair label="Phone" value={customer.phone} edit={edit} />
          <DetailPair label="DOB" value={customer.dob} edit={edit} />
          <DetailPair
            label="License Number"
            value={customer.drivers_license_number}
            edit={edit}
          />
          <DetailPair
            label="License Expiration"
            value={customer.drivers_license_exp}
            edit={edit}
            placeholder={"MM/DD/YYYY"}
          />
          {edit && (
            <DetailPair label="Address" value={customer.address} edit={edit} />
          )}
          <DetailPair label="Street" value={customer.street} />
          <DetailPair label="City" value={customer.city} />
          <DetailPair label="State" value={customer.state} />
          <DetailPair label="Zip" value={customer.zip} />
          <DetailPair label="Country" value={customer.country} />

          <LinkPair
            label="License Front"
            link={customer.drivers_license_front}
            edit={edit}
          />
          <LinkPair
            label="License Back"
            link={customer.drivers_license_back}
            edit={edit}
          />
          <DropDownBanner
            data={[
              customer.default_payment_method,
              ...(customer.payment_methods || []),
            ]}
          >
            <DetailPair
              label="Default Payment"
              value={
                customer.default_payment_method
                  ? customer.default_payment_method.brand +
                    " " +
                    customer.default_payment_method.last4
                  : "-"
              }
            />
          </DropDownBanner>

          <LinkPair
            label="Screening"
            value={customer.screening?.status || "-"}
            link={
              customer.screening &&
              `${process.env.REACT_APP_DIGISURE_URL}/portal/renters/${customer.screening.id}`
            }
          />

          <DetailPair
            label="Booking"
            value={
              customer.booking
                ? `${customer.booking.make} ${customer.booking.model} ${customer.booking.license}`
                : "-"
            }
            link={
              customer.booking && `/bookings/info?id=${customer.booking?.id}`
            }
          />

          <hr className="hr md:col-span-3" />

          <div
            onClick={() => navigate(`/payments/add?customerId=${customer.id}`)}
            className="btn-secondary"
          >
            Charge Customer
          </div>
          <div
            onClick={() => navigate(`/bookings/add?customerId=${customer.id}`)}
            className="btn-secondary"
          >
            Add Booking
          </div>
          <div onClick={() => setMessageModal(true)} className="btn-secondary">
            Send Message
          </div>
          {!customer.screening && (
            <Fragment>
              <div onClick={screeningHandler} className="btn-secondary">
                Start Screening
              </div>
              <div onClick={sendScreeningLinkHandler} className="btn-secondary">
                Send Screening Link
              </div>
            </Fragment>
          )}
          {customer.screening?.status === "pending" && (
            <div
              onClick={checkScreeningStatusHandler}
              className="btn-secondary"
            >
              Check Screening Status
            </div>
          )}
          {customer.screening && customer.screening?.status !== "pending" && (
            <div onClick={screeningHandler} className="btn-secondary">
              Restart Screening
            </div>
          )}
          {!customer.dnr && (
            <div onClick={addToDNRHandler} className="btn-secondary">
              Add to DNR
            </div>
          )}
          <div
            onClick={() =>
              navigate(`/customers/add-subscription?id=${customer.id}`)
            }
            className="btn-secondary"
          >
            {customer.plan?.active ? "Edit Subscription" : "Add Subscription"}
          </div>
          {warning && <p className="text-red-600">{warning}</p>}
        </div>
      )}
    </div>
  );
}
